import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, Result } from 'antd';
import { currentUserVar, UserPermissionsT } from '@/graphql/operations/user';
import { RequireAuth } from '@/components';
import { useReactiveVar } from '@apollo/client';
import { ResourceUIType } from '@/service/Api/types';
import { useTimedTokenValidation } from '@/utils/hooks/useTimedTokenValidation';

interface Props {
  children: React.ReactElement;
  props: {
    accessRoles: Array<'admin' | 'manager' | 'employee'>;
    resourceName: ResourceUIType;
  };
}

type UserRolePermissionT = Omit<UserPermissionsT, 'layoutAccess'>;

const permissionMap: Record<keyof UserRolePermissionT, 'admin' | 'manager' | 'employee'> = {
  isAdmin: 'admin',
  isManager: 'manager',
  isEmployee: 'employee',
};

export default function ProtectedRoute({ children, props }: Props) {
  const currentUser = useReactiveVar(currentUserVar);
  const location = useLocation();
  useTimedTokenValidation();

  const { layoutAccess, ...restPermissions } = currentUser.permissions;

  const userRoles = Object.keys(restPermissions)
    .filter((key) => restPermissions[key as keyof UserRolePermissionT])
    .map((key) => permissionMap[key as keyof UserRolePermissionT]);

  const resource = layoutAccess?.find(
    (access) => props.resourceName === access.resource && access.can.includes('read')
  );

  const userMatchAccessRole = userRoles.some((role) => props.accessRoles.includes(role));

  const hasAdminRole = userRoles.includes('admin');

  const isAdminDashboardAccess = props.resourceName === ResourceUIType.AdminDashboard;

  const adminDashboardEnabled =
    hasAdminRole && currentUser.organization.config.enableAdminDashboard && isAdminDashboardAccess;

  const hasAccess =
    adminDashboardEnabled ||
    !(layoutAccess?.length && (!resource || !userMatchAccessRole) && !location.pathname.startsWith('/v1'));

  if (!hasAccess) {
    return (
      <Result
        status="error"
        title="Access Denied"
        subTitle="Sorry, you are not authorized to access this page."
        extra={
          <Button type="primary">
            <Link to="/">Home</Link>
          </Button>
        }
      />
    );
  }

  return <RequireAuth>{children}</RequireAuth>;
}
